/**
 * The pixel work that happens AFTER a capture frame is frozen: black-frame detection and the JPEG
 * encode ladder. It lives in its own module — not inside the worker file — for two reasons:
 *
 *  1. The worker is a message shell that vitest/happy-dom cannot load at all (no OffscreenCanvas,
 *     no Worker), yet this is the logic actually worth testing: the ladder is what decides whether
 *     a photo survives Vertex's payload cap, and the black verdict is what decides whether a
 *     capture is retried or thrown away.
 *  2. The same code has to run in two places — the worker (OffscreenCanvas) and the main-thread
 *     fallback (DOM canvas) — and duplicating a size cap is how the two silently drift apart.
 *
 * Everything here is parameterised: it takes canvas-like objects and plain numbers, never a global.
 * The thresholds themselves stay in CameraPreviewComponent.vue, next to the measurements that
 * justify them, and travel in the job message.
 */

/**
 * Verdict on a downsampled RGBA sample: is this frame black?
 *
 * Identical arithmetic to the old in-component detector — a pixel counts as black when R+G+B is at
 * or under the threshold (real black frames carry tiny non-zero JPEG noise), and the frame counts
 * as black when at least `ratioThreshold` of the sample is. Kept as a plain function over a typed
 * array so it can be exercised without any canvas at all.
 */
export function isSampleBlack(pixels, rgbSumThreshold, ratioThreshold) {
  const totalPixels = pixels.length / 4;
  if (totalPixels === 0) return false;
  let blackPixels = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i] + pixels[i + 1] + pixels[i + 2] <= rgbSumThreshold) {
      blackPixels++;
    }
  }
  return blackPixels / totalPixels >= ratioThreshold;
}

/**
 * Draws a frozen frame into a 2D context at its natural size, applying the operator's configured
 * flips. Same transform order as the in-component draw it replaces (scale then translate, inside a
 * save/restore), so a flipped fleet device produces byte-comparable output through either path.
 */
export function drawFrame(ctx, source, width, height, flipHorizontally, flipVertically) {
  ctx.save();
  if (flipHorizontally) { ctx.scale(-1, 1); ctx.translate(-width, 0); }
  if (flipVertically) { ctx.scale(1, -1); ctx.translate(0, -height); }
  ctx.drawImage(source, 0, 0, width, height);
  ctx.restore();
}

/**
 * Encodes a canvas to JPEG and keeps it under `maxBytes` when it can, re-encoding the SAME pixels
 * at successively lower quality (no redraw, so no extra frame cost). Dropping quality is preferred
 * over downscaling: ticket codes stay at full resolution, which is what the operator and the OCR
 * both need.
 *
 * `canvas` only has to answer `convertToBlob({ type, quality })` — an OffscreenCanvas natively, a
 * DOM canvas through the adapter in photoEncoder.js, a stub in tests.
 *
 * Returns `{ blob, quality, steps, originalSize, overCap }`. A result still over the cap is
 * returned anyway (a photo the CV model may reject beats no photo at all) with `overCap: true`, so
 * the caller can say so loudly.
 */
export async function encodeWithCap(canvas, { quality, maxBytes, qualitySteps }) {
  let blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  if (!blob) return { blob: null, quality, steps: 0, originalSize: 0, overCap: false };
  const originalSize = blob.size;
  if (blob.size <= maxBytes) return { blob, quality, steps: 0, originalSize, overCap: false };

  let usedQuality = quality;
  let steps = 0;
  for (const step of qualitySteps) {
    const smaller = await canvas.convertToBlob({ type: 'image/jpeg', quality: step });
    if (!smaller) break;
    blob = smaller;
    usedQuality = step;
    steps++;
    if (blob.size <= maxBytes) break;
  }
  return { blob, quality: usedQuality, steps, originalSize, overCap: blob.size > maxBytes };
}
